<script setup>
import { ref } from 'vue';
import { router , Link} from '@inertiajs/vue3';
import JetDropdown from '@/Jetstream/Dropdown.vue';
import JetDropdownLink from '@/Jetstream/DropdownLink.vue';
import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink.vue';
import Locale from '@/Pages/Shared/Locale.vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    auditor: Boolean
});

// Get locale as set in cookie
let { locale } = useI18n({ useScope: 'global' });

const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const extraUserSessionData = router.page.props.ziggy.extraUserSessionData;
const header_menu = router.page.props.ziggy.front_end_header_menu;

let team_custom_name = programme.team_custom_name;
if (team_custom_name) {
    if(team_custom_name.constructor.name != 'Object') {
        team_custom_name = JSON.parse(team_custom_name);
    }
}
        
let default_team_name;

if (locale.value == 'cy') {
    default_team_name = 'tîm';
    
} else  {
    default_team_name = 'team';
}

let team_name = default_team_name;

if (team_custom_name) {
    team_name =  team_custom_name[locale.value] == null ? default_team_name : team_custom_name[locale.value];
}

let urlNamePrefix = '';
let availableLocales = [];

if (organisation) {
    urlNamePrefix = "gi.";
    availableLocales = organisation.available_locales;
} else if (!organisation && !programme) {
    urlNamePrefix = "platform.";
} else {
    availableLocales = programme.available_locales;
}

const showingNavigationDropdown = ref(false);

const switchToTeam = (team) => {
    router.put(route(urlNamePrefix+'current-team.update'), {
        team_id: team.id,
    }, {
        preserveState: false,
    });
};

const switchProgramme = (url) => {

    router.post(route(urlNamePrefix+'teams.switch-programme'), {
        redirectUrl: url,
    }, {
        preserveState: false,
        replace: true,
        preserveScroll: false,
        forceFormData: true,
        onSuccess: function() {
            document.location.reload(url);
        }

    });
}
const store = useStore();
const logout = () => {
    if (store.state.selectedLanguage && store.state.selectedLanguage == 'user') {
        store.commit('clearLanguageSelection');
        window.localStorage.removeItem('vuex');
    }
    router.post(route(urlNamePrefix+'logout'));
};

const organisationLogo = (organisation && organisation.logo) ? '/storage/'+ organisation.logo : null;
const programmeLogo = (programme && programme.logo) ? '/storage/'+ programme.logo : null;

</script>
<template>
    <header id="sos-app-header" class="">

    <div class="container mx-auto flex flex-row items-center justify-between w-full pt-5 pb-5">
        <Link :href="route(urlNamePrefix+'login')" class="logos flex flex-row items-center" v-if="urlNamePrefix != 'platform.'">
            <span v-if="$page.props.auth.user" class="sm:w-4"></span>
            <span class="image-wrapper sm:ml-5" v-if="programmeLogo">
                <img :src="programmeLogo" :alt="programme.name" class="logo-programme" />
            </span>
            <span class="image-wrapper" v-if="organisationLogo">
                <img :src="organisationLogo" :alt="organisation.name" class="logo-organisation sm:ml-10 sm:mr-10" />
            </span>
        </Link>
        <div id="sos-header-menu">

            <div class="sos-programme-dashboard-menu flex flex-row items-center" v-if="$page.props.auth.user && !auditor">

                <div class="sos-programme-dashboard-menu-item hidden sm:flex">
                <!-- Settings dropdown -->
                <JetDropdown align="right" width="48">
                    <template #trigger>
                        <button v-if="$page.props.jetstream.managesProfilePhotos" class="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:border-gray-300 transition">
                            <img class="h-16 w-16 rounded-full object-cover" :src="$page.props.auth.user.profile_photo_url" :alt="$page.props.auth.user.first_name + ' ' + $page.props.auth.user.last_name">
                        </button>

                        <span v-else class="inline-flex rounded-md">
                            <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition">
                                {{ $page.props.auth.user.first_name }} {{ $page.props.auth.user.last_name }}

                                <svg
                                    class="ml-2 -mr-0.5 h-4 w-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </span>
                    </template>

                    <template #content>
                        <!-- Account Management -->
                        <div class="block px-4 py-2 text-xs text-gray-400">
                            {{ $t('application.navigation.manage_account') }}
                        </div>

                        <JetDropdownLink :href="route(urlNamePrefix+'profile.show')">
                            {{ $t('application.navigation.profile') }}
                        </JetDropdownLink>

                        <JetDropdownLink v-if="$page.props.jetstream.hasApiFeatures" :href="route(urlNamePrefix+'api-tokens.index')">
                            {{ $t('application.navigation.api_tokens') }}
                        </JetDropdownLink>

                        <div class="border-t border-gray-100" />

                        <!-- Authentication -->
                        <form @submit.prevent="logout">
                            <JetDropdownLink as="button">
                                {{ $t('application.navigation.log_out') }}
                            </JetDropdownLink>
                        </form>
                    </template>
                </JetDropdown>
                </div>

                <div class="sos-programme-dashboard-menu-item hidden sm:flex">

                <!-- Teams Dropdown -->
                <JetDropdown v-if="$page.props.jetstream.hasTeamFeatures" align="right" width="60">
                    <template #trigger>
                        <span class="inline-flex rounded-md">
                            <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:bg-gray-50 active:bg-gray-50 transition">
                                <!-- {{ $page.props.auth.user.current_team.name }} -->
                                <span class="material-icons-outlined">group</span>
                                <svg
                                    class="ml-2 -mr-0.5 h-4 w-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </span>
                    </template>

                    <template #content>
                        <div class="w-60">
                            <!-- Team Management -->
                            <template v-if="$page.props.jetstream.hasTeamFeatures">
                                <div class="block px-4 py-2 text-xs text-gray-400 capitalize">
                                    {{ $t('application.navigation.manage_team', { team_name: team_name }) }} 
                                </div>

                                <!-- Team Settings -->
                                <div v-if="$page.props.auth.user.current_team" class="capitalize">
                                    <JetDropdownLink :href="route(urlNamePrefix+'teams.show', {team: $page.props.auth.user.current_team})">
                                        {{ $t('application.navigation.team_settings', { team_name: team_name }) }}
                                    </JetDropdownLink>
                                </div>

                                <div v-if="extraUserSessionData && extraUserSessionData.user_active_programmes.length > 1">

                                    <div class="border-t border-gray-100" />

                                    <div class="block px-4 py-2 text-xs text-gray-400">
                                        {{ $t('application.navigation.switch_toolkits') }}
                                    </div>

                                    <!-- Team Settings -->
                                    <div v-if="$page.props.auth.user.current_team">
                                        <template v-for="active_programme in extraUserSessionData.user_active_programmes">
                                            <JetDropdownLink  v-on:click="switchProgramme(active_programme.switchUrl)">
                                                <strong>{{active_programme.programme_name}}</strong><br />
                                                <div v-if="active_programme.organisation_name" v-html="active_programme.organisation_name" style="font-style: italic;" />
                                                {{active_programme.team_name}}
                                            </JetDropdownLink>
                                        </template>
                                    </div>

                                </div>
                            </template>
                        </div>
                    </template>
                </JetDropdown>
                </div>

                <!-- Hamburger -->
                <div class="-mr-2 flex items-center justify-end sm:hidden">
                    <button class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition" @click="showingNavigationDropdown = ! showingNavigationDropdown">
                        <svg
                            class="h-6 w-6"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                            <path
                                :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <div v-else-if="!auditor">
                <ul v-if="programme" class="md:flex md:flex-row">
                    <li>
                        <a :href="route(urlNamePrefix+'login')">{{$t('dashboard.login.home')}}</a>
                    </li>
                    <li v-for="item in header_menu">
                        <a :href="route(urlNamePrefix+'page', {page:item.slug})">{{item.title[$i18n.locale] ? item.title[$i18n.locale] : item.title[$i18n.fallbackLocale]}}</a>
                    </li>
                    <li v-if="programme.gi && organisation || !programme.gi" class="header-button-login">
                        <Link :href="route(urlNamePrefix+'login')" class="inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 transition sm:ml-4">
                            {{$t('dashboard.login.log_in')}}
                        </Link>
                    </li>
                </ul>
            </div>
            <div class="sos-programme-dashboard-menu flex flex-row items-center" v-if="Object.keys(availableLocales).length > 1">
                <!-- Language Dropdown -->
                <Locale :currentLocale="$i18n.locale" />
            </div>
        </div>
    </div>
    </header>

    <div v-if="$page.props.auth.user">
    <!-- Responsive Navigation Menu -->
    <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="sm:hidden">
        <div class="pt-2 pb-3 space-y-1">
            <JetResponsiveNavLink :href="route(urlNamePrefix+'dashboard')" :active="route().current(urlNamePrefix+'dashboard')">
                {{$t('dashboard.title')}}
            </JetResponsiveNavLink>
        </div>

        <!-- Responsive Settings Options -->
        <div class="pt-4 pb-1 border-t border-gray-200">
            <div class="flex items-center px-4">
                <div v-if="$page.props.jetstream.managesProfilePhotos" class="shrink-0 mr-3">
                    <img class="h-10 w-10 rounded-full object-cover" :src="$page.props.auth.user.profile_photo_url" :alt="$page.props.auth.user.first_name + ' ' + $page.props.auth.user.last_name">
                </div>

                <div>
                    <div class="font-medium text-base text-gray-800">
                        {{ $page.props.auth.user.first_name }} {{ $page.props.auth.user.last_name }}
                    </div>
                    <div class="font-medium text-sm text-gray-500">
                        {{ $page.props.auth.user.email }}
                    </div>
                </div>
            </div>

            <div class="mt-3 space-y-1">
                <JetResponsiveNavLink :href="route(urlNamePrefix+'profile.show')" :active="route().current(urlNamePrefix+'profile.show')">
                    {{ $t('application.navigation.profile') }}
                </JetResponsiveNavLink>

                <JetResponsiveNavLink v-if="$page.props.jetstream.hasApiFeatures" :href="route(urlNamePrefix+'api-tokens.index', {programme:programme, organisation:organisation})" :active="route().current(urlNamePrefix+'api-tokens.index')">
                    {{ $t('application.navigation.api_tokens') }}
                </JetResponsiveNavLink>

                <!-- Authentication -->
                <form method="POST" @submit.prevent="logout">
                    <JetResponsiveNavLink as="button">
                        {{ $t('application.navigation.log_out') }}
                    </JetResponsiveNavLink>
                </form>

                <!-- Team Management -->
                <template v-if="$page.props.jetstream.hasTeamFeatures">
                    <div class="border-t border-gray-200" />

                    <div class="block px-4 py-2 text-xs text-gray-400">
                        {{ $t('application.navigation.manage_team') }}
                    </div>

                    <!-- Team Settings -->
                    <div v-if="$page.props.auth.user.current_team">
                        <JetResponsiveNavLink :href="route('teams.show', {team:$page.props.auth.user.current_team})" :active="route().current(urlNamePrefix+'teams.show', {programme:programme, organisation:organisation})">
                            {{ $t('application.navigation.team_settings') }}
                        </JetResponsiveNavLink>
                    </div>

                    <!----<JetResponsiveNavLink v-if="$page.props.jetstream.canCreateTeams" :href="route('teams.create', {programme:programme, organisation:organisation})" :active="route().current(urlNamePrefix+'teams.create', {programme:programme, organisation:organisation})">
                        Create New Team
                    </JetResponsiveNavLink> -->

                    <div v-if="extraUserSessionData && extraUserSessionData.user_active_programmes.length > 1">

                        <div class="border-t border-gray-100" />

                        <div class="block px-4 py-2 text-xs text-gray-400">
                            {{ $t('application.navigation.switch_toolkits') }}
                        </div>

                        <!-- Team Settings -->
                        <div v-if="$page.props.auth.user.current_team">
                            <template v-for="active_programme in extraUserSessionData.user_active_programmes">
                                <JetDropdownLink  v-on:click="switchProgramme(active_programme.switchUrl)">
                                    <strong>{{active_programme.programme_name}}</strong><br />
                                    <div v-if="active_programme.organisation_name" v-html="active_programme.organisation_name" style="font-style: italic;" />
                                    {{active_programme.team_name}}
                                </JetDropdownLink>
                            </template>
                        </div>

                    </div>

                    <!-- Team Switcher -->
                    <!-- <div class="block px-4 py-2 text-xs text-gray-400">
                        Switch Teams
                    </div> -->

                    <!-- <template v-for="team in $page.props.auth.user.all_teams" :key="team.id">
                        <form @submit.prevent="switchToTeam(team)">
                            <JetResponsiveNavLink as="button">
                                <div class="flex items-center">
                                    <svg
                                        v-if="team.id == $page.props.auth.user.current_team_id"
                                        class="mr-2 h-5 w-5 text-green-400"
                                        fill="none"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    ><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <div>{{ team.name }}</div>
                                </div>
                            </JetResponsiveNavLink>
                        </form>
                    </template> -->
                </template>
            </div>
        </div>
    </div>
    </div>
</template>
